import {Injectable} from '@angular/core';
import {TenantOverview} from '../tenant/tenant.service';

/**
 * Enumeration of all available permissions in the system.
 * This provides type-safe permission checking.
 */
export enum Permission {
	// Users permissions
	USERS_READ = 'users:read',
	USERS_ADD = 'users:add',
	USERS_EDIT = 'users:edit',
	USERS_DELETE = 'users:delete',

	// Devices permissions (workstations and mobile devices)
	DEVICES_READ = 'devices:read',
	DEVICES_EDIT = 'devices:edit',

	// Members permissions (Kabeen platform member management)
	MEMBERS_READ = 'members:read',
	MEMBERS_ADD = 'members:add',
	MEMBERS_EDIT = 'members:edit',
	MEMBERS_DELETE = 'members:delete',

	// Tenant permissions
	TENANT_READ = 'tenant:read',
	TENANT_EDIT = 'tenant:edit',
	TENANT_DELETE = 'tenant:delete',

	// Agents permissions
	AGENTS_READ = 'agents:read',
	AGENTS_EDIT = 'agents:edit',

	// Announces permissions
	ANNOUNCES_DELETE = 'announces:delete',
	ANNOUNCES_ADD = 'announces:add',
	ANNOUNCES_EDIT = 'announces:edit',
	ANNOUNCES_READ = 'announces:read',

	// Categories permissions
	CATEGORIES_ADD = 'categories:add',
	CATEGORIES_EDIT = 'categories:edit',
	CATEGORIES_DELETE = 'categories:delete',

	// Tags permissions
	TAGS_ADD = 'tags:add',
	TAGS_DELETE = 'tags:delete',

	// Dashboard permissions
	FINANCE_DASHBOARD_READ = 'finance_dashboard:read',
	USAGE_DASHBOARD_READ = 'usage_dashboard:read',
	OPERATIONS_DASHBOARD_READ = 'operations_dashboard:read',
	ARCHITECTURE_DASHBOARD_READ = 'architecture_dashboard:read',
	TECHNOLOGY_DASHBOARD_READ = 'technology_dashboard:read',
	WORKSTATIONS_DASHBOARD_READ = 'workstations_dashboard:read',
	CUSTOM_DASHBOARD_READ = 'custom_dashboard:read',
	CUSTOM_DASHBOARD_EDIT = 'custom_dashboard:edit',
	CUSTOM_DASHBOARD_DELETE = 'custom_dashboard:delete',

	// Diagram permissions
	FLOW_MAPPING_DIAGRAM_READ = 'flow_mapping_diagram:read',
	FLOW_MAPPING_DIAGRAM_EDIT = 'flow_mapping_diagram:edit',
	FLOW_MAPPING_DIAGRAM_DELETE = 'flow_mapping_diagram:delete',
	CAPACITY_MAP_DIAGRAM_READ = 'capacity_map_diagram:read',
	CAPACITY_MAP_DIAGRAM_EDIT = 'capacity_map_diagram:edit',
	CAPACITY_MAP_DIAGRAM_DELETE = 'capacity_map_diagram:delete',
	APPLICATION_MATRIX_DIAGRAM_READ = 'application_matrix_diagram:read',
	APPLICATION_LIFECYCLE_DIAGRAM_READ = 'application_lifecycle_diagram:read',
	APPLICATION_COMPARISON_DIAGRAM_READ = 'application_comparison_diagram:read',
	NETWORK_MAPPING_DIAGRAM_READ = 'network_mapping_diagram:read',
	APPLICATION_QUADRANT_DIAGRAM_READ = 'application_quadrant_diagram:read',

	// Organisation permissions
	ORGANISATION_READ = 'organisation:read',
	ORGANISATION_ADD = 'organisation:add',
	ORGANISATION_EDIT = 'organisation:edit',
	ORGANISATION_DELETE = 'organisation:delete',

	// Applications permissions
	APPLICATIONS_READ = 'applications:read',
	APPLICATIONS_ADD = 'applications:add',
	APPLICATIONS_EDIT = 'applications:edit',
	APPLICATIONS_DELETE = 'applications:delete',
	APPLICATIONS_COMMENT = 'applications:comment',

	// Data permissions
	DATA_READ = 'data:read',
	DATA_ADD = 'data:add',
	DATA_EDIT = 'data:edit',
	DATA_DELETE = 'data:delete',

	// Servers permissions
	SERVERS_READ = 'servers:read',
	SERVERS_ADD = 'servers:add',
	SERVERS_EDIT = 'servers:edit',
	SERVERS_DELETE = 'servers:delete',

	// Networks permissions (networks and routers)
	NETWORKS_READ = 'networks:read',
	NETWORKS_ADD = 'networks:add',
	NETWORKS_EDIT = 'networks:edit',
	NETWORKS_DELETE = 'networks:delete',

	// Contracts permissions
	CONTRACTS_READ = 'contracts:read',
	CONTRACTS_ADD = 'contracts:add',
	CONTRACTS_EDIT = 'contracts:edit',
	CONTRACTS_DELETE = 'contracts:delete',

	// Finance permissions
	FINANCE_READ = 'finance:read',

	// Audit log permissions
	AUDIT_LOG_READ = 'audit_log:read',

	// Chat permissions
	CHAT_USE = 'chat:use',

	// API key permissions
	API_KEYS_READ = 'api_keys:read',
	API_KEYS_ADD = 'api_keys:add',
	API_KEYS_EDIT = 'api_keys:edit',
	API_KEYS_DELETE = 'api_keys:delete',

	// Integration permissions (admin role only)
	INTEGRATIONS_READ = 'integrations:read',
	INTEGRATIONS_ADD = 'integrations:add',
	INTEGRATIONS_EDIT = 'integrations:edit',
	INTEGRATIONS_DELETE = 'integrations:delete',
}

/** Whether the signed-in user holds `permission` on `tenant` (its permissions come from GET /tenants). */
export function tenantHasPermission(tenant: TenantOverview, permission: Permission | string): boolean {
	return (tenant.configuration.permissions || []).includes(permission);
}

/**
 * Permission checks against a given tenant. For the current workspace, read
 * `CurrentTenantStore.hasPermission()` / `hasAnyPermission()` (signals) instead.
 */
@Injectable({
	providedIn: 'root'
})
export class PermissionsService {

	/**
	 * Synchronously checks if a tenant has a specific permission.
	 * Useful when you already have the tenant object and need a synchronous check.
	 *
	 * @param tenant The tenant overview object
	 * @param permission The permission to check
	 * @returns boolean indicating if the user has the permission
	 */
	hasPermissionSync(tenant: TenantOverview, permission: Permission | string): boolean {
		return tenantHasPermission(tenant, permission);
	}


	/**
	 * Synchronously checks if a tenant has any of the specified permissions.
	 * Useful when you already have the tenant object and need a synchronous check.
	 *
	 * @param tenant The tenant overview object
	 * @param permissions Array of permissions to check
	 * @returns boolean indicating if the user has any of the permissions
	 */
	hasAnyPermissionSync(tenant: TenantOverview, permissions: (Permission | string)[]): boolean {
		const userPermissions = tenant.configuration.permissions || [];
		return permissions.some(permission => userPermissions.includes(permission));
	}

	/**
	 * Synchronously checks if a tenant has all of the specified permissions.
	 * Useful when you already have the tenant object and need a synchronous check.
	 *
	 * @param tenant The tenant overview object
	 * @param permissions Array of permissions to check
	 * @returns boolean indicating if the user has all permissions
	 */
	hasAllPermissionsSync(tenant: TenantOverview, permissions: (Permission | string)[]): boolean {
		const userPermissions = tenant.configuration.permissions || [];
		return permissions.every(permission => userPermissions.includes(permission));
	}

	/**
	 * Synchronously checks if the user has any write permissions (edit, add, or delete).
	 * This is a convenience method to determine if the user can make any changes.
	 *
	 * @param tenant The tenant overview object
	 * @returns boolean indicating if the user has any write permissions
	 */
	isEditorSync(tenant: TenantOverview): boolean {
		const permissions = tenant.configuration.permissions || [];
		return permissions.some(permission =>
			permission.endsWith(':edit') ||
			permission.endsWith(':add') ||
			permission.endsWith(':delete')
		);
	}

}

